import axios from 'axios';
import { MemberDetail } from '@the-hyoosik/nexus-api-connector-js/member';
import { refreshToken as requestRefreshToken } from '@the-hyoosik/nexus-api-connector-js/authentication';

import { paths } from 'src/routes/paths';

let retryCount = 0;

// ----------------------------------------------------------------------

function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);

  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

// ----------------------------------------------------------------------

export const tokenExpired = (exp: number) => {
  // eslint-disable-next-line prefer-const
  let expiredTimer;

  const currentTime = Date.now();

  // Test token expires after 10s
  // const timeLeft = currentTime + 10000 - currentTime; // ~10s
  const timeLeft = exp * 1000 - currentTime;

  clearTimeout(expiredTimer);

  expiredTimer = setTimeout(() => {
    alert('Token expired');

    localStorage.removeItem('accessToken');

    window.location.href = paths.auth.jwt.login;
  }, timeLeft);
};

// ----------------------------------------------------------------------

export const setLocal = (accessToken: string | null) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    // This function below will handle when token is expired
    const { exp } = jwtDecode(accessToken); // ~3 days by minimals server
    tokenExpired(exp);

    retryCount += 1;
  } else {
    localStorage.removeItem('accessToken');

    delete axios.defaults.headers.common.Authorization;
  }
};

// ----------------------------------------------------------------------

export const getRefreshToken = async () => {
  try {
    const result = await requestRefreshToken();
    retryCount = 0;
    setLocal(result.accessToken);
  } catch (err) {
    if (retryCount < 1) {
      console.error('access permission expired. plz login again.', err);

      localStorage.removeItem('accessToken');

      delete axios.defaults.headers.common.Authorization;
    }
  }
};

// ----------------------------------------------------------------------

export const hasManagerPrivilege = (member: MemberDetail | null): boolean => {
  if (!member) return false;
  return !!(member.isAreaManager || member.isGroupManager || member.isManager);
};

/**
 * 일반 멤버인지 확인 (관리자 권한이 없는 순수 멤버 -> 숙소 1개로 제한)
 */
export const isGeneralMember = (member: MemberDetail | null): boolean => {
  if (!member) return false;
  return member.isMember && !hasManagerPrivilege(member);
};

/**
 * 영업일 마감 후 금액 수정 권한 여부
 *
 * 이 함수에 포함된 권한을 가진 계정은 영업마감 이후에도
 * 결제 금액, 연박 금액, 기타 금액을 변경할 수 있습니다.
 * 변경 시 영업마감 데이터가 자동으로 재계산됩니다.
 *
 * 추후 다른 권한을 추가하려면 이 함수에 조건을 추가하세요.
 */
export const canOverrideBusinessDate = (member: MemberDetail | null): boolean => {
  if (!member) return false;
  return hasManagerPrivilege(member);
};

export {
  canEditOnBusinessDate,
  canCancelOnBusinessDate,
  isPastClosedBusinessDate,
  getMinAllowedBusinessDate,
  TEMPORARY_ALLOWED_PAST_DAYS,
  canReleaseNoShowOnBusinessDate,
  getMinAllowedBusinessDateForCancel,
  TEMPORARY_ALLOWED_PAST_DAYS_FOR_CANCEL,
  getMinAllowedBusinessDateForNoShowRelease,
  IS_TEMPORARY_PAST_RESERVATION_EDIT_ENABLED,
  TEMPORARY_ALLOWED_PAST_DAYS_FOR_NO_SHOW_RELEASE,
} from 'src/utils/policy/temporary-past-reservation-edit-policy';
